import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Elements } from "@stripe/react-stripe-js";
import LoadingSpinner from "../components/LoadingSpinner";
import ErrorMessage from "../components/ErrorMessage";
import StripeCheckout from "../components/billing/StripeCheckout";
import { getStripe, handleStripeError } from "../services/stripeService";
import api from "../services/api";

// Initialize Stripe
const stripePromise = getStripe();

const Subscribe = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [subscription, setSubscription] = useState({
    tier: state?.tier || "standard",
    accountLimit: state?.accountCount || 5,
    billingCycle: state?.billingCycle || "monthly",
  });
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate("/login", {
        state: { returnTo: "/subscribe", subscriptionData: state },
      });
    }
  }, [user, navigate, state]);

  const calculatePrice = (accountCount, tier) => {
    let basePrice;
    if (accountCount >= 10) {
      basePrice = accountCount * 10;
    } else if (accountCount >= 5) {
      basePrice = accountCount * 15;
    } else {
      basePrice = accountCount * 25;
    }

    if (tier === "premium") {
      basePrice *= 1.3;
    }

    return Math.round(basePrice * 100) / 100;
  };

  const monthlyPrice = calculatePrice(
    subscription.accountLimit,
    subscription.tier,
  );
  const annualPrice = Math.round(monthlyPrice * 12 * 0.9 * 100) / 100;
  const displayPrice =
    subscription.billingCycle === "annual" ? annualPrice : monthlyPrice;

  const handleSubscriptionChange = (field, value) => {
    setSubscription((prev) => ({ ...prev, [field]: value }));
  };

  const handlePaymentSuccess = (result) => {
    navigate("/billing", {
      state: {
        message:
          "Subscription created successfully! Your free trial has started.",
        type: "success",
        subscription: result,
      },
    });
  };

  const handlePaymentError = (error) => {
    console.error("Payment error:", error);
    // Show a user-friendly error message
    const errorMessage = handleStripeError(error);
    // You could set this in a state to show in the UI if needed
  };

  if (!user) {
    return <LoadingSpinner />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            Complete Your Subscription
          </h1>
          <p className="text-gray-600 mt-2">
            Start your 14-day free trial today. No commitment required.
          </p>
        </div>

        {/* Progress Indicator */}
        <div className="mb-8">
          <div className="flex items-center justify-center space-x-8">
            <div
              className={`flex items-center ${step >= 1 ? "text-primary-600" : "text-gray-400"}`}
            >
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                  step >= 1
                    ? "border-primary-600 bg-primary-600 text-white"
                    : "border-gray-300"
                }`}
              >
                1
              </div>
              <span className="ml-2 text-sm font-medium">Plan Selection</span>
            </div>

            <div
              className={`h-px w-16 ${step >= 2 ? "bg-primary-600" : "bg-gray-300"}`}
            />

            <div
              className={`flex items-center ${step >= 2 ? "text-primary-600" : "text-gray-400"}`}
            >
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                  step >= 2
                    ? "border-primary-600 bg-primary-600 text-white"
                    : "border-gray-300"
                }`}
              >
                2
              </div>
              <span className="ml-2 text-sm font-medium">Payment</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Plan Configuration */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">
                {step === 1 ? "Configure Your Plan" : "Payment Information"}
              </h2>

              {step === 1 ? (
                <div className="space-y-6">
                  {/* Tier Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Select Plan Tier
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={() =>
                          handleSubscriptionChange("tier", "standard")
                        }
                        className={`p-4 border-2 rounded-lg text-left transition-colors ${
                          subscription.tier === "standard"
                            ? "border-primary-500 bg-primary-50"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <div className="font-medium text-gray-900">
                          Standard
                        </div>
                        <div className="text-sm text-gray-600">
                          90-day retention
                        </div>
                      </button>

                      <button
                        onClick={() =>
                          handleSubscriptionChange("tier", "premium")
                        }
                        className={`p-4 border-2 rounded-lg text-left transition-colors ${
                          subscription.tier === "premium"
                            ? "border-primary-500 bg-primary-50"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <div className="font-medium text-gray-900">Premium</div>
                        <div className="text-sm text-gray-600">
                          365-day retention
                        </div>
                      </button>
                    </div>
                  </div>

                  {/* Account Limit */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Number of Social Media Accounts:{" "}
                      <span className="font-bold text-primary-600">
                        {subscription.accountLimit}
                      </span>
                    </label>
                    <input
                      type="range"
                      min="1"
                      max="50"
                      value={subscription.accountLimit}
                      onChange={(e) =>
                        handleSubscriptionChange(
                          "accountLimit",
                          parseInt(e.target.value),
                        )
                      }
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>1</span>
                      <span>25</span>
                      <span>50+</span>
                    </div>
                  </div>

                  {/* Billing Cycle */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Billing Cycle
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={() =>
                          handleSubscriptionChange("billingCycle", "monthly")
                        }
                        className={`p-4 border-2 rounded-lg text-left transition-colors ${
                          subscription.billingCycle === "monthly"
                            ? "border-primary-500 bg-primary-50"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <div className="font-medium text-gray-900">Monthly</div>
                        <div className="text-sm text-gray-600">
                          ${monthlyPrice}/month
                        </div>
                      </button>

                      <button
                        onClick={() =>
                          handleSubscriptionChange("billingCycle", "annual")
                        }
                        className={`p-4 border-2 rounded-lg text-left transition-colors ${
                          subscription.billingCycle === "annual"
                            ? "border-primary-500 bg-primary-50"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <div className="font-medium text-gray-900">
                          Annual
                          <span className="ml-2 text-xs bg-green-100 text-green-800 px-1 rounded">
                            Save 10%
                          </span>
                        </div>
                        <div className="text-sm text-gray-600">
                          ${annualPrice}/year
                        </div>
                      </button>
                    </div>
                  </div>

                  <button
                    onClick={() => setStep(2)}
                    className="w-full bg-primary-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-primary-700 transition-colors"
                  >
                    Continue to Payment
                  </button>
                </div>
              ) : stripePromise ? (
                <Elements stripe={stripePromise}>
                  <StripeCheckout
                    subscription={{
                      ...subscription,
                      displayPrice: displayPrice,
                      customerEmail: user?.email,
                    }}
                    onSuccess={handlePaymentSuccess}
                    onError={handlePaymentError}
                    disabled={loading}
                  />
                </Elements>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-600">Payment processing is currently unavailable.</p>
                  <p className="text-sm text-gray-500 mt-2">Please contact support for assistance.</p>
                </div>
              )}

              {step === 2 && (
                <button
                  onClick={() => setStep(1)}
                  className="mt-4 text-primary-600 hover:text-primary-700 text-sm font-medium"
                >
                  ← Back to Plan Selection
                </button>
              )}
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sticky top-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Order Summary
              </h3>

              <div className="space-y-3 mb-6">
                <div className="flex justify-between">
                  <span className="text-gray-600">Plan:</span>
                  <span className="font-medium text-gray-900">
                    {subscription.tier === "standard" ? "Standard" : "Premium"}
                  </span>
                </div>

                <div className="flex justify-between">
                  <span className="text-gray-600">Accounts:</span>
                  <span className="font-medium text-gray-900">
                    {subscription.accountLimit}
                  </span>
                </div>

                <div className="flex justify-between">
                  <span className="text-gray-600">Data Retention:</span>
                  <span className="font-medium text-gray-900">
                    {subscription.tier === "standard" ? "90" : "365"} days
                  </span>
                </div>

                <div className="flex justify-between">
                  <span className="text-gray-600">Billing:</span>
                  <span className="font-medium text-gray-900">
                    {subscription.billingCycle === "monthly"
                      ? "Monthly"
                      : "Annual"}
                  </span>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-4">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold text-gray-900">
                    Total:
                  </span>
                  <div className="text-right">
                    <span className="text-2xl font-bold text-gray-900">
                      ${displayPrice}
                    </span>
                    <div className="text-sm text-gray-600">
                      /
                      {subscription.billingCycle === "monthly"
                        ? "month"
                        : "year"}
                    </div>
                  </div>
                </div>

                {subscription.billingCycle === "annual" && (
                  <div className="mt-2 text-sm text-green-600">
                    Save ${(monthlyPrice * 12 - annualPrice).toFixed(2)}{" "}
                    annually
                  </div>
                )}
              </div>

              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start">
                  <svg
                    className="h-5 w-5 text-blue-500 mt-0.5 mr-2"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div className="text-sm text-blue-800">
                    <div className="font-medium">Free Trial Included</div>
                    <div className="mt-1">
                      Start your 14-day free trial today. You won't be charged
                      until the trial ends.
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-4 text-xs text-gray-500">
                <p>• Cancel anytime</p>
                <p>• Secure payment by Stripe</p>
                <p>• 24/7 customer support</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Subscribe;
